import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import Title from '../../component/Title'

const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024
const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg'])
const PRODUCT_DEFINITIONS = [
    { productCode: 'BCAA', label: 'BCAA' },
    { productCode: 'VITALITY_PILL', label: '활력환' },
]
const PRODUCT_LABELS = Object.fromEntries(
    PRODUCT_DEFINITIONS.map(({ productCode, label }) => [productCode, label])
)

const createEmptyProduct = () => ({
    coupangRunningOwnedProductId: null,
    productCode: 'BCAA',
    productName: '',
    productPrice: '',
    productImage: '',
    productImageFile: null,
    productImagePreview: '',
    productUrl: '',
    productId: '',
    itemId: '',
    vendorItemId: '',
    status: 0,
})

const normalizeProducts = (rows = []) => rows.map(row => ({
    ...row,
    productPrice: row.productPrice ?? '',
    status: [0, 1, 2].includes(Number(row.status)) ? Number(row.status) : 0,
}))

const filterProductsByDeletedState = (products, showDeleted) => products.filter(product => (
    showDeleted ? product.status === 2 : product.status !== 2
))

const validateProduct = (product) => {
    if (!PRODUCT_LABELS[product.productCode]) return '상품군을 선택해주세요.'
    if (!product.productName.trim()) return '상품명을 입력해주세요.'
    if (!Number.isInteger(Number(product.productPrice)) || Number(product.productPrice) <= 0) {
        return '상품 가격을 1원 이상으로 입력해주세요.'
    }
    if (!String(product.productImage || '').trim() && !product.productImageFile) {
        return '상품 이미지를 등록해주세요.'
    }
    if (!product.productUrl.trim()) return '쿠팡 상품 URL을 입력해주세요.'
    return ''
}

const validateImageFile = (file) => {
    if (!file) return '상품 이미지를 선택해주세요.'
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) return 'PNG, JPG, JPEG 이미지만 등록할 수 있습니다.'
    if (file.size > MAX_IMAGE_FILE_SIZE) return '상품 이미지는 10MB 이하만 등록할 수 있습니다.'
    return ''
}

const buildOwnedProductFormData = (product) => {
    const formData = new FormData()
    if (product.coupangRunningOwnedProductId) {
        formData.append('coupangRunningOwnedProductId', product.coupangRunningOwnedProductId)
    }
    formData.append('productCode', product.productCode)
    formData.append('productName', product.productName)
    formData.append('productPrice', product.productPrice)
    formData.append('productImage', product.productImage || '')
    formData.append('productUrl', product.productUrl)
    formData.append('status', product.status)
    if (product.productImageFile) {
        formData.append('productImageFile', product.productImageFile)
    }
    return formData
}

export default function CoupangRunningOwnedProducts() {
    const [products, setProducts] = useState([])
    const [editor, setEditor] = useState(createEmptyProduct)
    const [editorOpen, setEditorOpen] = useState(false)
    const [showDeleted, setShowDeleted] = useState(false)
    const [loading, setLoading] = useState(true)
    const [savingId, setSavingId] = useState(null)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')

    const activeCount = products.filter(product => product.status === 1).length
    const deletedCount = products.filter(product => product.status === 2).length
    const editing = Boolean(editor.coupangRunningOwnedProductId)

    const showMessage = useCallback((severity, message) => {
        setSeveritySnackbar(severity)
        setMessageSnackbar(message)
        setOpenSnackbar(true)
    }, [])

    const loadProducts = useCallback(async () => {
        setLoading(true)
        try {
            const { data } = await API.post('/api/v1/admin/coupangRunningOwnedProduct/getList', { includeDeleted: true })
            if (data.code !== 200) {
                throw new Error(data.message || '자사상품을 불러오지 못했습니다.')
            }
            setProducts(normalizeProducts(data.data))
        } catch (error) {
            showMessage('error', error.message || '자사상품을 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }, [showMessage])

    useEffect(() => {
        loadProducts()
    }, [loadProducts])

    const updateEditor = (field, value) => {
        setEditor(current => ({ ...current, [field]: value }))
    }

    const closeEditor = () => {
        if (savingId) return
        if (editor.productImagePreview) URL.revokeObjectURL(editor.productImagePreview)
        setEditor(createEmptyProduct())
        setEditorOpen(false)
    }

    const addProduct = () => {
        setEditor(createEmptyProduct())
        setEditorOpen(true)
    }

    const editProduct = (product) => {
        setEditor({ ...createEmptyProduct(), ...product })
        setEditorOpen(true)
    }

    const selectProductImage = (event) => {
        const file = event.target.files?.[0]
        const validationMessage = validateImageFile(file)
        event.target.value = ''
        if (validationMessage) {
            showMessage('warning', validationMessage)
            return
        }

        if (editor.productImagePreview) URL.revokeObjectURL(editor.productImagePreview)
        setEditor(current => ({
            ...current,
            productImageFile: file,
            productImagePreview: URL.createObjectURL(file),
        }))
    }

    const saveProduct = async () => {
        const validationMessage = validateProduct(editor)
        if (validationMessage) {
            showMessage('warning', validationMessage)
            return
        }
        setSavingId(editor.coupangRunningOwnedProductId || 'new')
        try {
            const { data } = await API.post(
                '/api/v1/admin/coupangRunningOwnedProduct/save',
                buildOwnedProductFormData(editor),
                { headers: { 'Content-Type': 'multipart/form-data' } },
            )
            if (data.code !== 200) {
                throw new Error(data.message || '자사상품을 저장하지 못했습니다.')
            }
            const savedProduct = normalizeProducts([data.data])[0]
            setProducts(current => {
                const exists = current.some(product => (
                    product.coupangRunningOwnedProductId === savedProduct.coupangRunningOwnedProductId
                ))
                return exists
                    ? current.map(product => (
                        product.coupangRunningOwnedProductId === savedProduct.coupangRunningOwnedProductId
                            ? savedProduct
                            : product
                    ))
                    : [...current, savedProduct]
            })
            if (editor.productImagePreview) URL.revokeObjectURL(editor.productImagePreview)
            setEditor(createEmptyProduct())
            setEditorOpen(false)
            showMessage('success', '자사상품을 저장했습니다.')
        } catch (error) {
            showMessage('error', error.message || '자사상품을 저장하지 못했습니다.')
        } finally {
            setSavingId(null)
        }
    }

    const changeStatus = async (product, nextStatus) => {
        const actionLabel = nextStatus === 1 ? '노출' : '비노출'
        if (!window.confirm(`${product.productName} 상품을 ${actionLabel} 상태로 변경할까요?`)) return

        setSavingId(product.coupangRunningOwnedProductId)
        try {
            const { data } = await API.post('/api/v1/admin/coupangRunningOwnedProduct/updateStatus', {
                coupangRunningOwnedProductId: product.coupangRunningOwnedProductId,
                status: nextStatus,
            })
            if (data.code !== 200) {
                throw new Error(data.message || '노출 상태를 변경하지 못했습니다.')
            }
            const updatedProduct = normalizeProducts([data.data])[0]
            setProducts(current => current.map(row => (
                row.coupangRunningOwnedProductId === updatedProduct.coupangRunningOwnedProductId
                    ? updatedProduct
                    : row
            )))
            if (editor.coupangRunningOwnedProductId === updatedProduct.coupangRunningOwnedProductId) {
                setEditor(current => ({ ...current, status: updatedProduct.status }))
            }
            showMessage('success', `${actionLabel} 상태로 변경했습니다.`)
        } catch (error) {
            showMessage('error', error.message || '노출 상태를 변경하지 못했습니다.')
        } finally {
            setSavingId(null)
        }
    }

    const deleteProduct = async (product) => {
        if (!window.confirm(`${product.productName} 상품을 삭제할까요? 삭제된 상품 보기에서 복구할 수 있습니다.`)) return
        setSavingId(product.coupangRunningOwnedProductId)
        try {
            const { data } = await API.post('/api/v1/admin/coupangRunningOwnedProduct/delete', {
                coupangRunningOwnedProductId: product.coupangRunningOwnedProductId,
            })
            if (data.code !== 200) throw new Error(data.message || '상품을 삭제하지 못했습니다.')
            const deletedProduct = normalizeProducts([data.data])[0]
            setProducts(current => current.map(row => (
                row.coupangRunningOwnedProductId === deletedProduct.coupangRunningOwnedProductId
                    ? deletedProduct
                    : row
            )))
            showMessage('success', '상품을 삭제했습니다.')
        } catch (error) {
            showMessage('error', error.message || '상품을 삭제하지 못했습니다.')
        } finally {
            setSavingId(null)
        }
    }

    const restoreProduct = async (product) => {
        if (!window.confirm(`${product.productName} 상품을 복구할까요? 복구 후 노출 후보는 OFF 상태입니다.`)) return
        setSavingId(product.coupangRunningOwnedProductId)
        try {
            const { data } = await API.post('/api/v1/admin/coupangRunningOwnedProduct/updateStatus', {
                coupangRunningOwnedProductId: product.coupangRunningOwnedProductId,
                status: 0,
            })
            if (data.code !== 200) throw new Error(data.message || '상품을 복구하지 못했습니다.')
            const restoredProduct = normalizeProducts([data.data])[0]
            setProducts(current => current.map(row => (
                row.coupangRunningOwnedProductId === restoredProduct.coupangRunningOwnedProductId
                    ? restoredProduct
                    : row
            )))
            showMessage('success', '상품을 복구했습니다.')
        } catch (error) {
            showMessage('error', error.message || '상품을 복구하지 못했습니다.')
        } finally {
            setSavingId(null)
        }
    }

    if (loading) return <Spiner />

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Title>APP 러너 필수템 자사상품</Title>
                        <Chip
                            label={`노출 후보 ${activeCount}개 · 앱 노출 1개`}
                            color={activeCount > 0 ? 'primary' : 'default'}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant={showDeleted ? 'contained' : 'outlined'} color="inherit" onClick={() => setShowDeleted(current => !current)}>
                            {showDeleted ? '일반 상품 보기' : `삭제된 상품 보기 (${deletedCount})`}
                        </Button>
                        <Button variant="contained" onClick={addProduct}>자사상품 등록</Button>
                    </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    노출 후보가 있으면 그중 1개를 랜덤 노출하고 쿠팡 파트너스 상품 4개를 함께 표시합니다. 후보가 없으면 파트너스 상품 5개를 표시합니다.
                </Typography>
            </Grid>

            {PRODUCT_DEFINITIONS.map(({ productCode, label }) => {
                const groupProducts = filterProductsByDeletedState(products, showDeleted)
                    .filter(product => product.productCode === productCode)
                return (
                    <Grid item xs={12} key={productCode}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>{label} {showDeleted ? '삭제 상품' : '등록 상품'} {groupProducts.length}개</Typography>
                            {groupProducts.length === 0 ? (
                                <Typography color="text.secondary">등록된 상품이 없습니다.</Typography>
                            ) : (
                                <Grid container spacing={2}>
                                    {groupProducts.map(product => {
                                        const saving = savingId === product.coupangRunningOwnedProductId
                                        return (
                                            <Grid item xs={12} md={6} key={product.coupangRunningOwnedProductId}>
                                                <Box sx={{ display: 'flex', gap: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                                    <Box component="img" src={product.productImage} alt={product.productName} sx={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 1 }} />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                            <Typography fontWeight={600} sx={{ wordBreak: 'break-all' }}>{product.productName}</Typography>
                                                            {showDeleted ? (
                                                                <Chip label="삭제됨" color="default" size="small" />
                                                            ) : (
                                                                <Switch
                                                                    checked={product.status === 1}
                                                                    disabled={saving}
                                                                    onChange={event => changeStatus(product, event.target.checked ? 1 : 0)}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Typography color="primary" sx={{ mt: 0.5 }}>{Number(product.productPrice).toLocaleString()}원</Typography>
                                                        {showDeleted ? (
                                                            <Button size="small" sx={{ mt: 1 }} disabled={saving} onClick={() => restoreProduct(product)}>복구</Button>
                                                        ) : (
                                                            <Box sx={{ mt: 1 }}>
                                                                <Button size="small" disabled={saving} onClick={() => editProduct(product)}>수정</Button>
                                                                <Button size="small" color="error" disabled={saving} onClick={() => deleteProduct(product)}>삭제</Button>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )
                                    })}
                                </Grid>
                            )}
                        </Paper>
                    </Grid>
                )
            })}
            <Dialog open={editorOpen} onClose={closeEditor} fullWidth maxWidth="md">
                <DialogTitle>{editing ? '자사상품 수정' : '자사상품 등록'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                            <TextField select fullWidth label="상품군" value={editor.productCode} disabled={Boolean(savingId)} onChange={event => updateEditor('productCode', event.target.value)}>
                                {PRODUCT_DEFINITIONS.map(product => (
                                    <MenuItem key={product.productCode} value={product.productCode}>{product.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <TextField fullWidth required label="상품명" value={editor.productName} disabled={Boolean(savingId)} onChange={event => updateEditor('productName', event.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField fullWidth required type="number" label="상품 가격" value={editor.productPrice} disabled={Boolean(savingId)} inputProps={{ min: 1 }} onChange={event => updateEditor('productPrice', event.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <TextField fullWidth required label="쿠팡 일반 상품 URL" value={editor.productUrl} disabled={Boolean(savingId)} helperText="검색·광고 추적 파라미터는 저장 시 자동 제거됩니다." onChange={event => updateEditor('productUrl', event.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>상품 이미지 *</Typography>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                                    <Box sx={{ width: 140, height: 140, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                                        {(editor.productImagePreview || editor.productImage) ? (
                                            <Box component="img" src={editor.productImagePreview || editor.productImage} alt="상품 미리보기" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">미리보기</Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <input
                                            accept="image/png,image/jpeg"
                                            id="coupang-running-owned-product-image"
                                            type="file"
                                            hidden
                                            disabled={Boolean(savingId)}
                                            onChange={selectProductImage}
                                        />
                                        <Button variant="outlined" component="label" htmlFor="coupang-running-owned-product-image" disabled={Boolean(savingId)}>
                                            이미지 등록
                                        </Button>
                                        <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
                                            {editor.productImageFile?.name || (editor.productImage ? '등록된 이미지 사용 중' : '선택된 이미지가 없습니다.')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            PNG, JPG, JPEG 형식의 10MB 이하 이미지를 등록해주세요.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={editor.status === 1} disabled={Boolean(savingId)} onChange={event => updateEditor('status', event.target.checked ? 1 : 0)} />}
                                label={editor.status === 1 ? '저장 후 노출 후보 ON' : '저장 후 노출 후보 OFF'}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button disabled={Boolean(savingId)} onClick={closeEditor}>취소</Button>
                    <Button variant="contained" disabled={Boolean(savingId)} onClick={saveProduct}>
                        {savingId ? '저장 중...' : '저장'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={() => setOpenSnackbar(false)} />
        </Grid>
    )
}

export {
    buildOwnedProductFormData,
    createEmptyProduct,
    filterProductsByDeletedState,
    normalizeProducts,
    validateImageFile,
    validateProduct,
}
